/**
 * Shared utilities for muscle-volume computations.
 * Used by both single-workout summary and program-level (weekly) summary.
 *
 * Supports per-user overrides:
 *  - extra_synonyms: [{synonym, target}]   → extends the default synonym map
 *  - custom_groups: [{key, label}]         → extends the default 10 canonical groups
 */

const DEFAULT_MUSCLE_GROUPS = [
  { key: "petto", label: "Petto" },
  { key: "schiena", label: "Schiena" },
  { key: "spalle", label: "Spalle" },
  { key: "bicipiti", label: "Bicipiti" },
  { key: "tricipiti", label: "Tricipiti" },
  { key: "quadricipiti", label: "Quadricipiti" },
  { key: "femorali", label: "Femorali" },
  { key: "glutei", label: "Glutei" },
  { key: "polpacci", label: "Polpacci" },
  { key: "core", label: "Core" },
];

const DEFAULT_SYNONYMS = {
  petto: ["petto", "pettorali", "chest"],
  schiena: ["schiena", "dorso", "dorsali", "back", "lats", "latissimus", "gran dorsale", "trapezio", "trapezi", "traps"],
  spalle: ["spalle", "deltoidi", "deltoide", "shoulders", "delts"],
  bicipiti: ["bicipiti", "bicipite", "biceps"],
  tricipiti: ["tricipiti", "tricipite", "triceps"],
  quadricipiti: ["quadricipiti", "quadricipite", "quad", "quadriceps"],
  femorali: ["femorali", "hamstrings", "bicipite femorale", "bicipiti femorali", "ischiocrurali"],
  glutei: ["glutei", "gluteo", "glutes"],
  polpacci: ["polpacci", "polpaccio", "calves", "calf"],
  core: ["core", "addome", "addominali", "abs", "obliqui", "lombari"],
};

/** Build the effective groups list (default + user custom) */
export function getEffectiveGroups(userMappings) {
  const custom = (userMappings?.custom_groups || [])
    .filter((g) => g && g.key && g.label)
    .map((g) => ({ key: String(g.key).toLowerCase(), label: g.label }));
  return [...DEFAULT_MUSCLE_GROUPS, ...custom];
}

/** Build the effective synonyms map (default + extra_synonyms) */
export function getEffectiveSynonyms(userMappings) {
  const map = {};
  Object.entries(DEFAULT_SYNONYMS).forEach(([k, arr]) => { map[k] = [...arr]; });
  // Ensure every custom group has at least its own key + label as synonyms
  (userMappings?.custom_groups || []).forEach((g) => {
    if (!g?.key) return;
    const key = String(g.key).toLowerCase();
    if (!map[key]) map[key] = [];
    map[key].push(key);
    if (g.label) map[key].push(String(g.label).toLowerCase());
  });
  (userMappings?.extra_synonyms || []).forEach((s) => {
    if (!s?.synonym || !s?.target) return;
    const syn = String(s.synonym).toLowerCase();
    const tgt = String(s.target).toLowerCase();
    if (!map[tgt]) map[tgt] = [];
    if (!map[tgt].includes(syn)) map[tgt].push(syn);
  });
  return map;
}

export const MUSCLE_GROUPS = DEFAULT_MUSCLE_GROUPS;
export const MUSCLE_GROUP_LABELS = Object.fromEntries(
  DEFAULT_MUSCLE_GROUPS.map((g) => [g.key, g.label])
);

export function normalizeMuscleGroup(raw, userMappings) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  const synonyms = getEffectiveSynonyms(userMappings);
  for (const [key, list] of Object.entries(synonyms)) {
    if (list.some((w) => s === w || s.includes(w))) return key;
  }
  return null;
}

/** Build a name-lowercase → { primary, secondaries[] } map from the library.
 *  Supports both the legacy singular `secondary_muscle_group` and the new
 *  `secondary_muscle_groups` list. Entries are normalised to canonical keys,
 *  deduped, and the primary is excluded from the secondaries. */
export function buildLibraryMap(exerciseLibrary, userMappings) {
  const map = {};
  (exerciseLibrary || []).forEach((ex) => {
    if (!ex?.name) return;
    const primary = normalizeMuscleGroup(ex.muscle_group, userMappings);
    const raw = Array.isArray(ex.secondary_muscle_groups) && ex.secondary_muscle_groups.length > 0
      ? ex.secondary_muscle_groups
      : (ex.secondary_muscle_group ? [ex.secondary_muscle_group] : []);
    const seen = new Set();
    const secondaries = [];
    raw.forEach((r) => {
      const k = normalizeMuscleGroup(r, userMappings);
      if (!k || k === primary || seen.has(k)) return;
      seen.add(k);
      secondaries.push(k);
    });
    map[ex.name.trim().toLowerCase()] = { primary, secondaries };
  });
  return map;
}

/**
 * Tally sets per muscle group for a list of exercises.
 *
 * @param {Array} exercises
 * @param {Object} libMap  result of buildLibraryMap
 * @param {Object} [opts]
 * @param {boolean} [opts.countSecondary=false]  include secondary muscles
 * @param {number}  [opts.secondaryWeight=0.5]   fractional weight applied to
 *                                                EACH secondary group
 */
export function tallyWorkout(exercises, libMap, opts = {}) {
  const countSecondary = opts.countSecondary === true;
  const secondaryWeight = typeof opts.secondaryWeight === "number"
    ? Math.max(0, Math.min(1, opts.secondaryWeight))
    : 0.5;
  const tally = {};
  let matched = 0;
  const total = exercises?.length || 0;
  (exercises || []).forEach((ex) => {
    if (!ex?.name) return;
    const entry = libMap[ex.name.trim().toLowerCase()];
    if (!entry) return;
    const sets = parseInt(ex.sets, 10) || 0;
    if (sets <= 0) return;
    if (entry.primary) {
      tally[entry.primary] = (tally[entry.primary] || 0) + sets;
      matched += 1;
    }
    if (countSecondary && entry.secondaries?.length) {
      entry.secondaries.forEach((g) => {
        tally[g] = (tally[g] || 0) + sets * secondaryWeight;
      });
    }
  });
  return { tally, matched, total };
}

/** Format a tally value: integer or one-decimal fraction (e.g., 7.5). */
export function formatVolume(n) {
  if (n === 0) return "0";
  if (Number.isInteger(n)) return String(n);
  // Round to nearest 0.5 then format
  const rounded = Math.round(n * 2) / 2;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function volumeLevel(total) {
  if (total >= 10) return "alto";
  if (total >= 5) return "medio";
  if (total >= 1) return "basso";
  return null;
}

export function weeklyVolumeLevel(total) {
  if (total >= 20) return "alto";
  if (total >= 10) return "medio";
  if (total >= 1) return "basso";
  return null;
}

export const LEVEL_STYLES = {
  basso: { bg: "bg-zinc-700/40", text: "text-zinc-300", ring: "ring-zinc-600/30" },
  medio: { bg: "bg-[#007AFF]/15", text: "text-[#007AFF]", ring: "ring-[#007AFF]/30" },
  alto: { bg: "bg-[#D4FF00]/15", text: "text-[#D4FF00]", ring: "ring-[#D4FF00]/40" },
};

export const DAYS_OF_WEEK = [
  { key: "Lunedi", short: "Lun" },
  { key: "Martedi", short: "Mar" },
  { key: "Mercoledi", short: "Mer" },
  { key: "Giovedi", short: "Gio" },
  { key: "Venerdi", short: "Ven" },
  { key: "Sabato", short: "Sab" },
  { key: "Domenica", short: "Dom" },
];
